const DEFAULT_CONSENT_UI_SELECTORS = [
  '#CybotCookiebotDialog',
  '#CybotCookiebotDialogBodyUnderlay',
  '#CybotCookiebotDialogBody',
  '#CybotCookiebotDialogHeader',
  '#CybotCookiebotDialogPoweredbyCybot',
  '#cookiebanner',
  '#shopify-pc__banner',
];

const ACCEPT_CONSENT_ARRAY = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function restoreCustomCookieBannerContent() {
  document
    .querySelectorAll('cookie-banner .cookie_banner_container')
    .forEach((el) => {
      el.style.removeProperty('display');
      el.style.removeProperty('visibility');
      el.style.removeProperty('pointer-events');
    });
}

function removeCookiebotDialogStyles() {
  document.getElementById('CookiebotDialogStyle')?.remove();
}

function isCookiebotUiNode(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  if (
    node.id === 'CookiebotDialogStyle' ||
    node.id === 'cookiebanner' ||
    node.id?.startsWith('CybotCookiebot')
  ) {
    return true;
  }

  return !!node.querySelector?.(
    '#CookiebotDialogStyle, #cookiebanner, [id^="CybotCookiebot"]'
  );
}

export function suppressDefaultConsentUi() {
  if (typeof window.Cookiebot?.hide === 'function') {
    window.Cookiebot.hide();
  }

  removeCookiebotDialogStyles();

  DEFAULT_CONSENT_UI_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
    });
  });

  restoreCustomCookieBannerContent();
  document.body?.classList.remove('CybotCookiebotDialogActive');
}

function observeDefaultConsentUi() {
  if (!document.body) {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          continue;
        }

        if (isCookiebotUiNode(node)) {
          suppressDefaultConsentUi();
          return;
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

export function initDefaultConsentUiSuppression() {
  suppressDefaultConsentUi();

  ['CookiebotOnDialogInit', 'CookiebotOnDialogDisplay'].forEach((eventName) => {
    window.addEventListener(eventName, suppressDefaultConsentUi);
  });

  if (document.body) {
    observeDefaultConsentUi();
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        observeDefaultConsentUi();
      },
      { once: true }
    );
  }
}

export function hasStoredConsentResponse() {
  return (
    !!localStorage.getItem('__rmcp2') || window.Cookiebot?.hasResponse === true
  );
}

export function waitForCookiebotConsentReady() {
  if (window.Cookiebot?.consent) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    window.addEventListener('CookiebotOnConsentReady', () => resolve(), {
      once: true,
    });
  });
}

export function getCookiebotConsent() {
  const consent = window.Cookiebot?.consent;

  return {
    necessary: consent?.necessary ?? true,
    preferences: consent?.preferences ?? false,
    statistics: consent?.statistics ?? false,
    marketing: consent?.marketing ?? false,
  };
}

export function syncLocalStorageFromConsent({
  preferences,
  statistics,
  marketing,
}) {
  if (!preferences && !statistics && !marketing) {
    localStorage.setItem('__rmcp2', 'declined');
    return;
  }

  if (preferences && statistics && marketing) {
    localStorage.setItem('__rmcp2', ACCEPT_CONSENT_ARRAY);
    return;
  }

  localStorage.setItem('__rmcp2', 'saved');
}

export function submitCustomConsent(preferences, statistics, marketing) {
  if (typeof window.Cookiebot?.submitCustomConsent === 'function') {
    window.Cookiebot.submitCustomConsent(
      preferences,
      statistics,
      marketing
    );
  }

  syncLocalStorageFromConsent({ preferences, statistics, marketing });
  suppressDefaultConsentUi();
}

export function persistAcceptedConsent() {
  submitCustomConsent(true, true, true);
  window.__rmcp2 = ACCEPT_CONSENT_ARRAY;
  dismissCookieBannerIfPresent();
  dispatchConsentChanged();
}

export function persistDeclinedConsent() {
  submitCustomConsent(false, false, false);
  dismissCookieBannerIfPresent();
  dispatchConsentChanged();
}

export function persistSavedConsent({ preferences, statistics, marketing }) {
  submitCustomConsent(preferences, statistics, marketing);
  dismissCookieBannerIfPresent();
  dispatchConsentChanged();
}

export function dismissCookieBannerIfPresent() {
  const cookie_banner = document.querySelector('cookie-banner');

  if (typeof cookie_banner?.dismiss === 'function') {
    cookie_banner.dismiss();
  }
}

function dispatchConsentChanged() {
  document.dispatchEvent(
    new CustomEvent('cookie-banner:statechange', {
      bubbles: true,
      detail: { consentPending: false, visible: false },
    })
  );
}

export function openCookiePreferences() {
  document.dispatchEvent(
    new CustomEvent('drawer-cookie-preferences:open', { bubbles: true })
  );
}

function isCookiebotChangeConsentControl(element) {
  const control = element?.closest?.('a, button');

  if (!control) {
    return false;
  }

  const href = control.getAttribute('href') || '';

  if (href === '#cookie-preferences') {
    return true;
  }

  const controlId = control.id || '';

  if (
    controlId === 'CookieDeclarationChangeConsent' ||
    (controlId.startsWith('CookieDeclaration') &&
      /ChangeConsent/i.test(controlId))
  ) {
    return true;
  }

  return /javascript:\s*(?:Cookiebot|CookieConsent)\.renew/i.test(href);
}

function initCookiePreferencesLinks() {
  document.addEventListener('click', (e) => {
    if (!isCookiebotChangeConsentControl(e.target)) {
      return;
    }

    e.preventDefault();
    openCookiePreferences();
  });
}

initDefaultConsentUiSuppression();
initCookiePreferencesLinks();
